import { useCallback, useRef } from "react";
import { cn } from "@/lib/utils";
import dayjs from "dayjs";
import { ThreadsQueryThread } from "@/app/Threads/GraphQL/threadQueries";
import { useUser } from "@/lib/Auth/useUser";
import { ThreadMembers } from "@/app/Threads/Components/ThreadMembers";
import { ClosedBadge } from "@/app/Threads/Components/ThreadHeader";
import { ThreadActionMenuItems } from "@/app/Threads/Components/ThreadActionMenuItems";
import { ThreadQuickCloseButton } from "@/app/Threads/Components/ThreadQuickCloseButton";
import { usePendingThreadIds } from "@/app/Threads/hooks/usePendingThreadIds";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  getSearchTokens,
  HighlightedText,
} from "@/app/Threads/utils/searchHighlight";
import { hasUnread } from "@/app/Threads/utils/hasUnread";
import { useTranslations } from "use-intl";

type Props = {
  threads: ThreadsQueryThread[];
  selectedId: string | null;
  onSelect: (thread: ThreadsQueryThread) => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  search?: string;
};

export function ThreadList({
  threads,
  selectedId,
  onSelect,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  search = "",
}: Props) {
  const searchTokens = getSearchTokens(search);
  const pendingThreadIds = usePendingThreadIds();

  const observerRef = useRef<IntersectionObserver | null>(null);

  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (!node || !hasNextPage || isFetchingNextPage || !fetchNextPage) {
        return;
      }
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      });
      observerRef.current.observe(node);
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  if (threads.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-sm text-gray-500">
        No threads yet
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 divide-y divide-gray-100 overflow-y-auto">
      {threads.map((thread, index) => (
        <ThreadListItem
          key={thread.id}
          thread={thread}
          isSelected={selectedId === thread.id}
          isPending={pendingThreadIds.has(thread.id)}
          onSelect={onSelect}
          searchTokens={searchTokens}
          rowRef={index === threads.length - 1 ? lastItemRef : undefined}
        />
      ))}
      {isFetchingNextPage ? (
        <div className="flex items-center justify-center p-4 text-sm text-gray-500">
          Loading...
        </div>
      ) : null}
    </div>
  );
}

function ThreadListItem({
  thread,
  isSelected,
  isPending,
  onSelect,
  searchTokens,
  rowRef,
}: {
  thread: ThreadsQueryThread;
  isSelected: boolean;
  isPending: boolean;
  onSelect: (thread: ThreadsQueryThread) => void;
  searchTokens: string[];
  rowRef?: (node: HTMLDivElement | null) => void;
}) {
  const me = useUser();
  const t = useTranslations("thread.notes");

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          ref={rowRef}
          role="button"
          tabIndex={0}
          aria-busy={isPending}
          onClick={() => onSelect(thread)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSelect(thread);
            }
          }}
          className={cn(
            // pr-11 is the gutter ThreadQuickCloseButton holds, for its hover
            // action and for the spinner that replaces it.
            "group relative flex w-full cursor-pointer flex-col gap-1.5 py-5 pl-4 pr-11 text-left transition-colors hover:bg-gray-50",
            isSelected && "bg-blue-50 hover:bg-blue-50",
            // The row is showing what the server has yet to answer for.
            isPending && "opacity-50",
          )}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ThreadableReference
                thread={thread}
                searchTokens={searchTokens}
              />
              {thread.closed ? <ClosedBadge /> : null}
            </div>
            <ThreadMembers
              thread={thread}
              size={5}
              searchTokens={searchTokens}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {hasUnread(thread) ? (
                <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500" />
              ) : null}
              <span className="text-xs font-medium text-gray-500">
                {getThreadTitle(thread)}
              </span>
            </div>
            {thread.latestMessage ? (
              <span className="text-xs text-gray-500">
                {dayjs.utc(thread.latestMessage.created_at).local().fromNow()}
              </span>
            ) : null}
          </div>
          <div className="mb-2 flex items-center gap-1"></div>
          {thread.latestMessage ? (
            <p className="truncate text-xs text-gray-500">
              {thread.latestMessage.is_admin_note ? (
                <span className="mr-1 inline-block rounded bg-amber-200 px-1 py-px align-middle text-[10px] font-medium text-amber-900">
                  {t("listBadge")}
                </span>
              ) : null}
              {thread.latestMessage.user_id === me.id ? "You: " : ""}
              {thread.latestMessage.body ?? "Sent an attachment"}
            </p>
          ) : (
            <p className="text-xs italic text-gray-400">No messages yet</p>
          )}
          <ThreadQuickCloseButton thread={thread} isPending={isPending} />
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-52">
        <ThreadActionMenuItems thread={thread} />
      </ContextMenuContent>
    </ContextMenu>
  );
}

function ThreadableReference({
  thread,
  searchTokens,
}: {
  thread: ThreadsQueryThread;
  searchTokens: string[];
}) {
  const workOrderReference =
    thread.threadable?.__typename === "WorkOrder"
      ? thread.threadable.reference
      : undefined;
  return (
    <p className="self-start text-sm font-medium text-gray-900">
      <HighlightedText text={workOrderReference} tokens={searchTokens} />
    </p>
  );
}

function getThreadTitle(thread: ThreadsQueryThread): string {
  const threadable = thread.threadable;
  if (!threadable) return "";

  if (threadable.__typename === "WorkOrder") {
    return `${threadable.departureAddress.city} -> ${threadable.deliveryAddress.city}`;
  }

  return "";
}
